import { useEffect, useMemo, useState } from 'react';
import {
  useInitProgramPricingMutation,
  useRemoveProgramPricingMutation,
  useUpdateProgramPricingMutation,
} from 'generated/graphql';
import { muiForm } from '@client/new/helpers/forms';
import { Control, useForm } from 'react-hook-form';
import { Actions, Async, FSpread } from '@client/new/helpers/types';
import auth from '@client/components/auth';
import toast from 'react-hot-toast';
import { Collection } from '../collection.domain';
import { applyPercentageAdjustment } from './utils';
import { PATHS } from '@client/routes';
import { useDiscounts } from '@client/new/pages/promotions/model/Discounts.model';

type PayloadMap = {
  savePrice: never;
  removePricing: never;
};

export type PricingAction = Actions<PayloadMap>;
export type PricingModelDispatcher = FSpread<PayloadMap>;
export type Pricing = {
  price: number;
  playbookFee: number;
};

export type FormValues = {
  price: number;
  playbookFee?: number;
  discountPercent?: number;
};

export type UsePricingModel = {
  couponsCount: number;
  createPromoPageLink: string;
  viewPromoPageLink: string;
  creatorId: number;
  pricing: Pricing | null;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  control: Control<FormValues, any, FormValues>;
  disabledSavePrice: boolean;
  showDiscount: boolean;
  playbookFeePrice: string;
  dispatch: PricingModelDispatcher;
  canRemovePricing: boolean;
  submitting: boolean;
  showFee: boolean;
  readonly: boolean;
};

export type PricingModelParams = {
  creatorId: number;
  collection: Collection;
  onPriceSubmitted: () => void;
  readonly: boolean;
};

export const usePricingModel = ({
  creatorId,
  collection,
  onPriceSubmitted,
  readonly,
}: PricingModelParams): Async<UsePricingModel> => {
  const [submitting, setSubmitting] = useState(false);

  const { data: couponData } = useDiscounts(creatorId);

  const couponsCount: number = useMemo(() => {
    if (couponData.type === 'fetched')
      return (couponData.value || []).filter((c) =>
        c.paidProductIds?.includes(collection.id),
      ).length;
    return 0;
  }, [collection.id, couponData]);

  const meta = collection?.exclusiveProgramMetadata;

  const pricing = useMemo(
    () => ({
      price: meta?.originalPrice ? meta?.originalPrice : meta?.price || 0,
      playbookFee: meta?.playbookFee || 0,
    }),
    [meta],
  );

  const [initPricing] = useInitProgramPricingMutation();
  const [updateProgramPricing] = useUpdateProgramPricingMutation();
  const [removeProgramPricing] = useRemoveProgramPricingMutation();

  const { watch, control, reset, getValues } = muiForm(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    useForm<any>({
      defaultValues: {
        price: '0',
        playbookFee: 0,
      },
    }),
  );

  useEffect(() => {
    reset({
      price: pricing?.price ? String(pricing?.price / 100) : '0',
      playbookFee: pricing?.playbookFee ?? 0,
    });
  }, [pricing, reset]);

  const dispatch: PricingModelDispatcher = (type, ...args) => {
    const action = { type, payload: args[0] } as PricingAction;
    switch (action.type) {
      case 'savePrice':
        savePrice();
        return null;
      case 'removePricing':
        removePricing();
        return null;
    }
  };

  const savePrice = () => {
    const price = getValues('price');
    setSubmitting(true);
    if (!collection.exclusiveProgramMetadata?.price) {
      initPricing({
        variables: {
          programId: collection.id || 0,
          price: Math.round(Number(price) * 100),
        },
      })
        .then(() => {
          onPriceSubmitted();
          toast.success('Price updated successfully');
        })
        .catch((e) => {
          console.error(e);
          toast.error(e.message);
        })
        .finally(() => {
          setSubmitting(false);
        });
      return;
    }
    const fee = getValues('playbookFee');
    setSubmitting(true);
    updateProgramPricing({
      variables: {
        programId: collection.id || 0,
        price: Math.round(Number(price) * 100),
        fee,
      },
    })
      .then(() => {
        onPriceSubmitted();
        toast.success('Price updated successfully');
      })
      .catch((e) => {
        console.error(e);
        toast.error(e.message);
      })
      .finally(() => {
        setSubmitting(false);
      });
  };

  const removePricing = () => {
    setSubmitting(true);
    removeProgramPricing({
      variables: {
        programId: collection.id || 0,
      },
    })
      .then(() => {
        onPriceSubmitted();
        toast.success('Price removed successfully');
      })
      .catch((e) => {
        console.error(e);
        toast.error(e.message);
      })
      .finally(() => {
        setSubmitting(false);
      });
  };

  const price = watch('price');
  const playbookFee = watch('playbookFee');

  const canRemovePricing =
    !!meta?.price && (auth.isAdministrator() || auth.isModerator());

  const disabledSavePrice =
    !price ||
    (price * 100 === meta?.price && playbookFee === meta?.playbookFee);

  return {
    type: 'fetched',
    value: {
      couponsCount,
      createPromoPageLink: PATHS.PROMOTIONS(creatorId, {
        mode: 'create',
        programId: '' + collection.id,
      }),
      viewPromoPageLink: PATHS.PROMOTIONS(creatorId, {
        mode: 'list',
        search: '"' + collection.name + '"',
      }),
      creatorId,
      pricing,
      control,
      disabledSavePrice,
      showDiscount: !!meta?.price,
      playbookFeePrice: applyPercentageAdjustment(playbookFee, Number(price)),
      dispatch,
      canRemovePricing,
      submitting,
      showFee: auth.isAdministrator(),
      readonly,
    },
  };
};
